import { logComponentError } from '@ms/yammer-diagnostics-state';
import type { ManagedApiCaching, ManagedApiRequest } from '@ms/yammer-libs-api';
import { executeManagedApi } from '@ms/yammer-libs-api';
import { getStaticConfig } from '@ms/yammer-libs-static-config';
import type { EcsPersistKey } from '@ms/yammer-libs-static-config';
import { getAADApplication, reportError } from '@ms/yammer-telemetry';
import { getTelemetryHostingConfig } from '@ms/yammer-telemetry-store';
import type { HostingEnvironment, RequestInitiator } from '@ms/yammer-telemetry-support';
import { acquireToken, getTenantIdFromToken, getUserIdFromToken } from '@ms/yammer-web-authenticators';

import { initializeExperiments } from './initialization';
import type { EcsPayload } from './normalization';
import { normalizeEcsPayload } from './normalization';
import type { EcsServiceSnapshot } from './store';
import { experimentsStore } from './store';
import { reportEcsEtagMapping } from './telemetry';

const apiName = 'GetEcsFeatureFlags';
const publicEcsHost = 'https://ecs.office.com';

type EcsEnvironment = 'Development' | 'Dogfood' | 'Microsoft' | 'Production';

const hostingEnvironmentToEcsEnvironment: Readonly<Record<string, EcsEnvironment>> = {
  dev: 'Development',
  SDFV2: 'Dogfood',
  edog: 'Dogfood',
  prodbubble: 'Dogfood',
  Dogfood: 'Dogfood',
  dogfood: 'Dogfood',
  Microsoft: 'Microsoft',
  MSIT: 'Microsoft',
  PROD: 'Production',
  FirstRelease: 'Production',
  WW: 'Production',
} satisfies Record<HostingEnvironment, EcsEnvironment>;

interface EcsRequestQuery {
  readonly [key: string]: string;
  readonly Application: string;
  readonly Environment: EcsEnvironment;
  readonly UserId: string;
  readonly TenantId: string;
}

export interface EcsUserContext {
  readonly userId: string;
  readonly tenantId: string;
}

export interface EcsServiceSnapshotPublicationResult {
  readonly snapshot: EcsServiceSnapshot;
  readonly publicationError?: unknown;
}

export interface LoadEcsTreatmentsOptions {
  readonly caching?: Omit<ManagedApiCaching, 'key'>;
  readonly persistKey?: EcsPersistKey;
  readonly initiator?: RequestInitiator;
  readonly userContext?: EcsUserContext;
  readonly publishServiceSnapshot?: (snapshot: EcsServiceSnapshot) => void;
  readonly finalizeServiceSnapshotPublication?: (result: EcsServiceSnapshotPublicationResult) => void;
}

const getEcsEnvironment = (): EcsEnvironment => {
  const hostingConfig = getTelemetryHostingConfig();

  return hostingConfig == null
    ? 'Production'
    : (hostingEnvironmentToEcsEnvironment[hostingConfig.hostingEnvironment] ?? 'Production');
};

const getEcsRequestQuery = (userId: string, tenantId: string): EcsRequestQuery => ({
  Application: getAADApplication(),
  Environment: getEcsEnvironment(),
  UserId: userId,
  TenantId: tenantId,
});

const authenticateEcsRequest = async (userContext: EcsUserContext | undefined): Promise<EcsRequestQuery> => {
  if (userContext !== undefined) {
    return getEcsRequestQuery(userContext.userId, userContext.tenantId);
  }

  const yammerResource = getStaticConfig().adal.resources.yammer;
  const token = await acquireToken({ scopes: [yammerResource] });

  return getEcsRequestQuery(getUserIdFromToken(token), getTenantIdFromToken(token));
};

const getCacheKey = (query: EcsRequestQuery): string => apiName.concat(JSON.stringify(query));

const createServiceLoadError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)));

const failServiceLoad = (error: unknown): void => {
  experimentsStore.getState().failServiceLoad(createServiceLoadError(error));
};

const reportServiceSnapshotPublicationError = (error: unknown): void => {
  const publicationError = createServiceLoadError(error);

  logComponentError('Experiments', publicationError);
  reportError({ error: publicationError });
};

const publishEcsPayload = (payload: EcsPayload): EcsServiceSnapshot => {
  const snapshot = normalizeEcsPayload(payload);

  reportEcsEtagMapping(snapshot.ecsConfig);
  experimentsStore.getState().completeServiceLoad(snapshot);

  return snapshot;
};

const publishCompatibilityServiceSnapshot = (
  snapshot: EcsServiceSnapshot,
  options: LoadEcsTreatmentsOptions | undefined
): void => {
  let publicationError: unknown;
  let publicationFailed = false;
  let finalizationError: unknown;
  let finalizationFailed = false;

  try {
    options?.publishServiceSnapshot?.(snapshot);
  } catch (error) {
    publicationError = error;
    publicationFailed = true;
  }

  try {
    options?.finalizeServiceSnapshotPublication?.({
      snapshot,
      ...(publicationFailed ? { publicationError } : undefined),
    });
  } catch (error) {
    finalizationError = error;
    finalizationFailed = true;
  }

  if (publicationFailed) {
    if (finalizationFailed) {
      reportServiceSnapshotPublicationError(finalizationError);
    }

    throw publicationError;
  }

  if (finalizationFailed) {
    throw finalizationError;
  }
};

export const loadEcsTreatments = async (options?: LoadEcsTreatmentsOptions): Promise<void> => {
  experimentsStore.getState().beginServiceLoad();
  let initialSnapshot: EcsServiceSnapshot;

  try {
    const initializationResult = initializeExperiments(options?.persistKey);
    const staticConfig = getStaticConfig();
    const ecsHost = staticConfig.hosts.ecs;
    const authenticatedQuery = await authenticateEcsRequest(options?.userContext);
    const query = ecsHost === publicEcsHost ? { ...authenticatedQuery, Cloud: 'Public' } : authenticatedQuery;
    const request: ManagedApiRequest = {
      method: 'GET',
      host: ecsHost,
      path: '/config/v1/YammerClients/0.0.1',
      apiName,
      requestType: 'Ecs',
      initiator: options?.initiator,
      query,
      shouldSkipTokenCheck: true,
    };
    const caching =
      options?.caching === undefined
        ? undefined
        : {
            ...options.caching,
            key: getCacheKey(query),
          };
    const performanceMetadata =
      options?.persistKey === undefined
        ? undefined
        : {
            actionName: 'initializeEcsFeatureFlags',
            cacheExists: initializationResult.cacheExists,
          };
    let hasPublishedInitialResponse = false;
    const { response } = await executeManagedApi<EcsPayload>({
      request,
      caching,
      performanceMetadata,
      responseWritten: (writtenResponse) => {
        if (hasPublishedInitialResponse && caching?.strategy === 'ReadCacheAndRevalidateWithFulfill') {
          let writtenSnapshot: EcsServiceSnapshot;

          try {
            writtenSnapshot = publishEcsPayload(writtenResponse);
          } catch (error) {
            failServiceLoad(error);

            return;
          }

          try {
            publishCompatibilityServiceSnapshot(writtenSnapshot, options);
          } catch (error) {
            reportServiceSnapshotPublicationError(error);
          }
        }
      },
    });

    initialSnapshot = publishEcsPayload(response);
    hasPublishedInitialResponse = true;
  } catch (error) {
    failServiceLoad(error);
    throw error;
  }

  publishCompatibilityServiceSnapshot(initialSnapshot, options);
};
